import * as React from 'react';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogTitle, { type DialogTitleProps } from '@mui/material/DialogTitle';
import DialogContent, { type DialogContentProps } from '@mui/material/DialogContent';
import DialogActions, { type DialogActionsProps } from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
export type ModalTone = 'danger' | 'info' | 'success' | 'warning' | 'primary';

/** reactstrap widths are 300/500/800/1140; MUI's xs/sm/md/lg are 444/600/900/1200. */
const SIZE_TO_MAX_WIDTH: Record<ModalSize, NonNullable<DialogProps['maxWidth']>> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
};

const TONE_TO_PALETTE: Record<ModalTone, 'error' | 'info' | 'success' | 'warning' | 'primary'> = {
  danger: 'error',
  info: 'info',
  success: 'success',
  warning: 'warning',
  primary: 'primary',
};

const ToneContext = React.createContext<ModalTone | undefined>(undefined);

export interface ModalProps extends Omit<DialogProps, 'open' | 'onClose' | 'maxWidth' | 'title'> {
  isOpen: boolean;
  /** Called on close X, backdrop click and Escape (subject to `backdrop`/`keyboard`). */
  toggle?: () => void;
  /** Convenience mode: renders the header/body/footer for you. */
  title?: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Replaces reactstrap's className='modal-*': tints the title bar. */
  tone?: ModalTone;
  /** reactstrap: 'static' or false disables backdrop-click close. */
  backdrop?: boolean | 'static';
  /** reactstrap: false disables Escape close. */
  keyboard?: boolean;
  /** reactstrap onClosed: fires after the exit transition. */
  onClosed?: () => void;
  /**
   * reactstrap's vertical centring. Dialogs open from the top of the viewport by default
   * (see the MuiDialog override in the theme); pass this to centre one instead.
   */
  centered?: boolean;
  scrollable?: boolean;
  /** Overrides the size mapping, e.g. maxWidth={false} for a 95vw modal. */
  maxWidth?: DialogProps['maxWidth'];
  testId?: string;
}

/**
 * MUI Dialog behind reactstrap's Modal API, so the 90 files rendering `<Modal isOpen toggle>`
 * convert by import swap. Also accepts `title`/`footer` for new code.
 */
export function Modal({
  isOpen,
  toggle,
  title,
  footer,
  size = 'md',
  tone,
  backdrop = true,
  keyboard = true,
  onClosed,
  centered,
  scrollable: _scrollable,
  maxWidth,
  testId,
  children,
  slotProps,
  ...rest
}: ModalProps) {
  const handleClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick' && (backdrop === 'static' || backdrop === false)) return;
    if (reason === 'escapeKeyDown' && !keyboard) return;
    toggle?.();
  };

  const convenience = title !== undefined || footer !== undefined;

  return (
    <ToneContext.Provider value={tone}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth={maxWidth ?? SIZE_TO_MAX_WIDTH[size]}
        scroll='paper'
        data-testid={testId}
        slotProps={{
          ...slotProps,
          transition: { ...slotProps?.transition, onExited: onClosed },
          ...(centered && { container: { sx: { alignItems: 'center' } } }),
        }}
        {...rest}
      >
        {title !== undefined && <ModalHeader toggle={toggle}>{title}</ModalHeader>}
        {convenience ? <ModalBody>{children}</ModalBody> : children}
        {footer !== undefined && <ModalFooter>{footer}</ModalFooter>}
      </Dialog>
    </ToneContext.Provider>
  );
}

export interface ModalHeaderProps {
  children: React.ReactNode;
  /** When provided, renders the close X — the same rule reactstrap uses. */
  toggle?: () => void;
  className?: string;
  /** Merged after the tone styles, for headers that need to pin themselves to a scrolling body. */
  sx?: DialogTitleProps['sx'];
}

export function ModalHeader({ children, toggle, className, sx }: ModalHeaderProps) {
  const tone = React.useContext(ToneContext);
  const palette = tone ? TONE_TO_PALETTE[tone] : undefined;
  return (
    <DialogTitle
      className={className}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: toggle ? 1 : 3,
          ...(palette && { bgcolor: `${palette}.main`, color: `${palette}.contrastText` }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {toggle && (
        <IconButton aria-label='Close' onClick={toggle} size='small' sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
}

export function ModalBody(props: DialogContentProps) {
  return <DialogContent {...props} />;
}

export function ModalFooter(props: DialogActionsProps) {
  return <DialogActions {...props} />;
}
